import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatDate } from 'src/adapters/dates.adapter';
import { RefundIcon } from 'src/components';
import { SText } from 'src/components/SText';
import { organizationCurrencySelector } from 'src/features/appSettings';
import { currencySymbolMapping } from 'src/helpers';
import { IdModel } from 'src/types';
import { RefundItem } from 'src/types/public/public-data-model.types';

type Props = {
  testID?: string;
  isOpen: boolean;
  refund: RefundItem;
  onSelect: (refundId: IdModel) => void;
  label: string;
};

export const RefundHistoryPublicItem: React.FC<Props> = ({
  onSelect,
  isOpen,
  refund,
  testID = 'RefundHistoryPublicItem',
  label,
}) => {
  const currency = useSelector(organizationCurrencySelector);
  const onSelectHandler = useCallback(() => {
    onSelect(refund.id);
  }, [onSelect, refund]);

  const dateForma = useMemo(() => {
    const date = new Date(refund.createdAt);
    return formatDate(date);
  }, [refund]);

  return (
    <div data-testid={testID} className="">
      <button
        className="px-4 py-3 rounded-md shadow-md bg-white my-2 bloc w-full justify-between text-left flex-row flex items-center w-full"
        onClick={onSelectHandler}
      >
        <div className="flex justify-center items-center">
          <RefundIcon />
          <SText
            label={label}
            classNames="text-md mx-2"
            translatingOptions={{ date: dateForma.date, hour: dateForma.time }}
          />
        </div>
        <div>
          <span className="text-primary text-2xl font-bold">{isOpen ? '-' : '+'}</span>
        </div>
      </button>
      {isOpen && (
        <div className="bg-white px-2 py-2">
          {!!refund.colis.length && <SText label="public.your-colis" classNames="text-primary my-1" />}

          <table className="my-4 border-1 border-black border-solid w-full">
            {refund.colis.map(({ coli }) => {
              return (
                <>
                  <tr key={coli.id} className="space-x-2 w-full">
                    <td className="text-left  p-1 text-sm md:text-md">
                      <SText label={coli.tracker} classNames="" />
                    </td>
                    <td className="text-left  p-1 text-sm md:text-md">
                      <SText label={coli?.selectedCatalogPrice?.name ?? coli?.selectedEnum ?? '-'} classNames="" />
                    </td>
                    <td className="text-left  p-1 text-sm md:text-md">
                      <SText
                        label={coli.selectedCatalogPrice ? '' : 'public.mesured'}
                        classNames=""
                        translatingOptions={{
                          value: coli.selectedCatalogPrice ? coli.measureValue : Number(coli.measureValue).toFixed(2),
                        }}
                      />
                    </td>
                    <td className="text-left  p-1 text-sm md:text-md">
                      <SText
                        label="public.price"
                        classNames=""
                        translatingOptions={{
                          price: coli.price.toFixed(2),
                          currency: currencySymbolMapping(coli.priceCurrency ?? currency) ?? '€',
                        }}
                      />
                    </td>
                  </tr>
                </>
              );
            })}

            <div className="my-4 w-full">
              <div className="flex flex-row justify-between w-full">
                <SText
                  label="public.refund-paid"
                  translatingOptions={{
                    value: refund.totalRefund.toFixed(2),
                    currency: currencySymbolMapping(refund.currency ?? currency) ?? '€',
                  }}
                  classNames="block"
                />
                <SText
                  label="public.bdc-create-at"
                  translatingOptions={{ date: dateForma.date }}
                  classNames="block text-sm text-gray-400 md:text-md"
                />
              </div>
            </div>
          </table>
        </div>
      )}
    </div>
  );
};
